import { useMemo } from 'react';
import { Radio, Checkbox, Switch, Typography, Spin, Empty, Divider, Space } from 'antd';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useStationObservations } from '../useStationObservations';
import type { WidgetSettingsProps } from './types';

const { Text } = Typography;

const CHART_OPTIONS = [
  { value: 'temperature', label: 'Temperature' },
  { value: 'wind', label: 'Wind' },
  { value: 'humidity-pressure', label: 'Humidity / Pressure' },
  { value: 'rain', label: 'Rain' },
  { value: 'wind-direction', label: 'Wind Direction' },
] as const;

const ALL_CHART_IDS = CHART_OPTIONS.map((o) => o.value);

export const StationChartsSettings = ({
  config,
  onChange,
}: WidgetSettingsProps) => {
  const { selectedEvent } = useEventSelection();
  const { stations, loading } = useStationObservations();

  const selectedStation = (config.selectedStation as string | undefined) ?? '';
  const visibleCharts = (config.visibleCharts as string[] | undefined) ?? ALL_CHART_IDS;
  const showLegend = !(config.hideLegend as boolean | undefined);
  const allChartsSelected = visibleCharts.length === ALL_CHART_IDS.length ||
    ALL_CHART_IDS.every((id) => visibleCharts.includes(id));

  const availableStations = useMemo(
    () => stations.map((s) => s.station_reference),
    [stations],
  );

  const handleStationChange = (station: string) => {
    onChange({ selectedStation: station });
  };

  const handleChartToggle = (chartId: string, checked: boolean) => {
    let next: string[];
    if (checked) {
      next = [...visibleCharts, chartId];
    } else {
      next = visibleCharts.filter((id) => id !== chartId);
    }
    if (next.length === 0) return;
    onChange({ visibleCharts: next });
  };

  const handleSelectAllCharts = (checked: boolean) => {
    onChange({ visibleCharts: checked ? [...ALL_CHART_IDS] : [ALL_CHART_IDS[0]] });
  };

  const renderStationSelection = () => {
    if (!selectedEvent) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select an event to configure stations"
        />
      );
    }

    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Loading stations...</Text>
          </div>
        </div>
      );
    }

    if (availableStations.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No stations available for this event"
        />
      );
    }

    return (
      <>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          Choose which station to display.
        </Text>

        <Radio.Group
          value={selectedStation || availableStations[0]}
          onChange={(e) => handleStationChange(e.target.value)}
        >
          <Space direction="vertical" size={4}>
            {availableStations.map((station) => (
              <Radio key={station} value={station}>
                <Text style={{ fontSize: 13 }}>Station {station}</Text>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </>
    );
  };

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Station
      </Text>

      {renderStationSelection()}

      <Divider style={{ margin: '16px 0' }} />

      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Visible Charts
      </Text>

      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        Choose which charts to display. At least one must be selected.
      </Text>

      <Space direction="vertical" size={4}>
        <Checkbox
          checked={allChartsSelected}
          indeterminate={!allChartsSelected && visibleCharts.length > 0}
          onChange={(e) => handleSelectAllCharts(e.target.checked)}
        >
          <Text style={{ fontSize: 13 }}>All charts</Text>
        </Checkbox>

        {CHART_OPTIONS.map((opt) => (
          <Checkbox
            key={opt.value}
            checked={visibleCharts.includes(opt.value)}
            onChange={(e) => handleChartToggle(opt.value, e.target.checked)}
            style={{ marginLeft: 20 }}
          >
            <Text style={{ fontSize: 13 }}>{opt.label}</Text>
          </Checkbox>
        ))}
      </Space>

      <Divider style={{ margin: '16px 0' }} />

      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Display
      </Text>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Switch
          size="small"
          checked={showLegend}
          onChange={(checked) => onChange({ hideLegend: !checked })}
        />
        <Text style={{ fontSize: 13 }}>Show legend</Text>
      </div>
    </div>
  );
};
